import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { format } from 'date-fns';

const InvoiceList = ({ 
  invoices, 
  filters, 
  onFilterChange, 
  onDownloadInvoice, 
  onSendEmail,
  onRefresh 
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-md overflow-hidden">
      <div className="p-4 border-b border-border flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h2 className="text-xl font-heading font-semibold text-foreground">
          Invoice List
        </h2>
        
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={filters?.paymentStatus}
            onChange={(e) => onFilterChange?.({ ...filters, paymentStatus: e?.target?.value })}
            className="px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>

          <input
            type="date"
            value={filters?.startDate}
            onChange={(e) => onFilterChange?.({ ...filters, startDate: e?.target?.value })}
            className="px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Start Date"
          />

          <input
            type="date"
            value={filters?.endDate}
            onChange={(e) => onFilterChange?.({ ...filters, endDate: e?.target?.value })}
            className="px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="End Date"
          />

          <Button
            onClick={onRefresh}
            variant="outline"
            iconName="RefreshCw"
            iconSize={16}
            className="text-sm"
          >
            Refresh
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Invoice #
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Member
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Due Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Amount
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {invoices?.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-12 text-center">
                  <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No invoices found</p>
                </td>
              </tr>
            ) : (
              invoices?.map((invoice) => (
                <tr key={invoice?.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-foreground">
                      {invoice?.invoice_number}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      {invoice?.user_profiles?.avatar_url ? (
                        <img
                          src={invoice?.user_profiles?.avatar_url}
                          alt={invoice?.user_profiles?.full_name || 'Member'}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon name="User" size={16} className="text-primary" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {invoice?.user_profiles?.full_name || 'Unknown'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {invoice?.user_profiles?.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-foreground">
                      {format(new Date(invoice?.billing_date), 'MMM dd, yyyy')}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-foreground">
                      {format(new Date(invoice?.due_date), 'MMM dd, yyyy')}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-foreground">
                      ${parseFloat(invoice?.total_amount || 0)?.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                      getStatusColor(invoice?.payment_status)
                    }`}>
                      {invoice?.payment_status?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onDownloadInvoice?.(invoice?.id)}
                        className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                        title="Download PDF"
                      >
                        <Icon name="Download" size={16} className="text-primary" />
                      </button>
                      <button
                        onClick={() => onSendEmail?.(invoice?.id)}
                        className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                        title="Send Email"
                      >
                        <Icon name="Mail" size={16} className="text-primary" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoiceList;